/**
 * Геометрия и цвета знака Lumos — одно описание для логотипа (`LumosMark`) и
 * индикатора загрузки (`SunLoader`).
 *
 * Знак: светящееся ядро с анаморфным бликом, вокруг — наклонная орбита,
 * разорванная на две дуги, и две планеты на дугах. Поле 100 × 100, центр ядра
 * в (50, 50). Те же числа — в `androidtv/tools/make-artwork.py` (иконки,
 * favicon, исходники SVG) и в приставке (`ui/Brand.kt`,
 * `player/SunLoaderView.kt`): правя форму здесь, поправьте и там.
 *
 * Орбита — окружность, сжатая по вертикали (`SQUASH`) и повёрнутая (`TILT`,
 * минус — правый конец выше). Углы на орбите — в системе окружности до сжатия,
 * по часовой стрелке от правого конца большой оси (ось y вниз, как в SVG).
 * Так же устроен и индикатор: дуги и планеты вращаются в этой окружности, а
 * сжатие и наклон — неподвижная обёртка, и путь по эллипсу получается сам.
 */

export const TILT = -4.5;
export const SQUASH = 0.8825;

/** Полный знак — для крупного (от 64 px): ореолы, блик, тонкие дуги. */
export const FULL = {
  core: 20.5,
  halo: 47,
  orbit: 43.4,
  arc: 1.4,
  arcGlow: 4,
  head: 11,
  tail: 131,
  planetAt: 45,
  planet: 4.6,
  planetHalo: 11,
  flare: 64,
  flareW: 0.8,
  flareGlow: 60,
  flareGlowW: 2.2,
} as const;

/**
 * Упрощённый знак — для 16–48 px: без ореолов и блика, дуги и планеты толще.
 * Тонкая гаснущая нить в шестнадцати пикселях рассыпается в пыль, а орбита
 * должна читаться.
 */
export const SMALL = {
  core: 21,
  orbit: 41,
  arc: 6,
  head: 14,
  tail: 118,
  planetAt: 45,
  planet: 8.5,
} as const;

export type Stop = readonly [offset: number, color: string, opacity: number];

const GLOW = '#FF9C1E';
export const ARC_COLOR = '#F7AE2E';

/** Ядро: горячее в середине, сверху светлее, снизу янтарнее — центр градиента выше и левее. */
export const CORE_STOPS: readonly Stop[] = [
  [0, '#FFF9E6', 1],
  [0.15, '#FFF0B9', 1],
  [0.35, '#FFE27D', 1],
  [0.6, '#FFD150', 1],
  [0.85, '#FEC030', 1],
  [1, '#FCB826', 1],
];

export const PLANET_STOPS: readonly Stop[] = [
  [0, '#FFF1C8', 1],
  [0.45, '#FFD467', 1],
  [1, '#F5A926', 1],
];

/** Ореол ядра: плотный у края и быстро гаснет — свет, а не туман. */
export const HALO_STOPS: readonly Stop[] = [
  [0, GLOW, 0.75],
  [0.43, GLOW, 0.72],
  [0.55, GLOW, 0.36],
  [0.65, GLOW, 0.2],
  [0.78, GLOW, 0.08],
  [0.9, GLOW, 0.025],
  [1, GLOW, 0],
];

export const PLANET_HALO_STOPS: readonly Stop[] = [
  [0, GLOW, 0.6],
  [0.42, GLOW, 0.45],
  [0.7, GLOW, 0.12],
  [1, GLOW, 0],
];

/** Блик: ярче всего у края ядра, к концам гаснет до нуля. */
export const FLARE_STOPS: readonly Stop[] = [
  [0, '#FFBE46', 0],
  [0.12, '#FFBE46', 0.2],
  [0.25, '#FFC855', 0.55],
  [0.34, '#FFDE8C', 1],
  [0.5, '#FFF0BE', 1],
  [0.66, '#FFDE8C', 1],
  [0.75, '#FFC855', 0.55],
  [0.88, '#FFBE46', 0.2],
  [1, '#FFBE46', 0],
];

export const FLARE_GLOW_STOPS: readonly Stop[] = FLARE_STOPS.map(([o, c, a]) => [o, c, a * 0.25] as const);

type Point = readonly [number, number];

/** Точка орбиты: окружность → сжатие → наклон → сдвиг в центр. */
export function orbitPoint(deg: number, radius: number, cx = 50, cy = 50, squash = SQUASH, tilt = TILT): Point {
  const a = (deg * Math.PI) / 180;
  const x = radius * Math.cos(a);
  const y = radius * squash * Math.sin(a);
  const t = (tilt * Math.PI) / 180;
  return [cx + x * Math.cos(t) - y * Math.sin(t), cy + x * Math.sin(t) + y * Math.cos(t)];
}

const n = (v: number): string => {
  const text = v.toFixed(2).replace(/\.?0+$/, '');
  return text === '-0' ? '0' : text;
};

/** Дуга эллипса орбиты от `head` до `tail` по часовой. */
export function arcPath(head: number, tail: number, radius: number, cx = 50, cy = 50, squash = SQUASH, tilt = TILT): string {
  const [x0, y0] = orbitPoint(head, radius, cx, cy, squash, tilt);
  const [x1, y1] = orbitPoint(tail, radius, cx, cy, squash, tilt);
  const large = (((tail - head) % 360) + 360) % 360 > 180 ? 1 : 0;
  return `M${n(x0)} ${n(y0)}A${n(radius)} ${n(radius * squash)} ${n(tilt)} ${large} 1 ${n(x1)} ${n(y1)}`;
}

/**
 * Яркость дуги вдоль хорды «голова → хвост»: ярче всего у планеты, хвост
 * гаснет долго, голова — быстро, как след за летящей планетой. Градиент по
 * хорде, а не по дуге: на дуге меньше 180° проекция на хорду монотонна, и
 * глаз разницы не видит.
 */
export function arcGradient(
  head: number,
  tail: number,
  at: number,
  radius: number,
  small: boolean,
  cx = 50,
  cy = 50,
  squash = SQUASH,
  tilt = TILT,
): { from: Point; to: Point; stops: readonly Stop[] } {
  const from = orbitPoint(head, radius, cx, cy, squash, tilt);
  const to = orbitPoint(tail, radius, cx, cy, squash, tilt);
  const p = orbitPoint(at, radius, cx, cy, squash, tilt);
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const tp = ((p[0] - from[0]) * dx + (p[1] - from[1]) * dy) / (dx * dx + dy * dy);
  const stops: readonly Stop[] = small
    ? [
        [0, ARC_COLOR, 0.6],
        [tp, ARC_COLOR, 1],
        [1, ARC_COLOR, 0.3],
      ]
    : [
        [0, ARC_COLOR, 0],
        [tp * 0.55, ARC_COLOR, 0.85],
        [tp, ARC_COLOR, 1],
        [tp + (1 - tp) * 0.35, ARC_COLOR, 0.7],
        [1, ARC_COLOR, 0],
      ];
  return { from, to, stops };
}

/** Луч блика — веретено: толстый в середине, сходится в точку на концах. */
export function spindlePath(cx: number, cy: number, halfLength: number, halfWidth: number): string {
  return (
    `M${n(cx - halfLength)} ${n(cy)}Q${n(cx)} ${n(cy - 2 * halfWidth)} ${n(cx + halfLength)} ${n(cy)}` +
    `Q${n(cx)} ${n(cy + 2 * halfWidth)} ${n(cx - halfLength)} ${n(cy)}Z`
  );
}

/** Вершина градиента ядра: выше и левее центра, радиус с запасом — край не обрезан. */
export const coreGradient = (r: number, cx = 50, cy = 50): { cx: number; cy: number; r: number } => ({
  cx: cx - r * 0.08,
  cy: cy - r * 0.14,
  r: r * 1.12,
});

/** Блик планеты сверху слева, как у ядра. */
export const planetGradient = (r: number, cx: number, cy: number): { cx: number; cy: number; r: number } => ({
  cx: cx - r * 0.3,
  cy: cy - r * 0.35,
  r: r * 1.25,
});
